import {
  Directive, Input, Output, EventEmitter, ContentChild,
  ViewContainerRef, TemplateRef, EmbeddedViewRef, OnInit
} from '@angular/core';
import { map, catchError } from 'rxjs/operators';

import { StackLocation } from './types';


// PAGE CLASS ===============================================================================
// Stores information about each page that is accessible from the template

export class CarouselPage  {
  constructor(
    public $implicit: any,
    public index: number,
    private parent: PagesRendererDirective
  ) { };

  get isActive() { return this.parent.page === this.index; }

  get isFirst() { return this.index === 0; }
  get isLast() { return this.index === this.parent.pageCount - 1; }
}


// PAGE RENDERER DIRECTIVE ==================================================================
// Similar to ngFor, but renders items as stacked full-screen pages

@Directive({ selector: '[carouselPages]' })
export class PagesRendererDirective implements OnInit {
    // Initialization
    private isInitialized = false;
  // Get the input data (using loop syntax)
  private collection: Array<any>;
  private _lastPageCount: number;
  private _page = 0;
  public pageCountChange = new EventEmitter<number>();
  private pageWidth = 0;
  private pageHeight = 0;
  private views: EmbeddedViewRef<any>[] = [];

  // Angular Injection
  constructor(
    private viewContainer: ViewContainerRef,
    private template: TemplateRef<CarouselPage>
  ) { }


  // LOOP TEMPLATING
  @Input() set carouselPagesOf(coll: Array<any>) {
    this.collection = coll;
    if (this.isInitialized) {
      this.ClearDOM();
      this.CreateDOM();
    }
  }


  ngOnInit() {
    this.isInitialized = true;
    this.CreateDOM();
  }


  // PAGINATION

  // Calculate page count from the loop

  public get pageCount() {
    const count = (this.collection) ? this.collection.length : 0;
    if (this._lastPageCount !== count) {
      this.pageCountChange.emit(count);
    }
    return count;
  }


  // Page access

  public set page(page: number) { this.SetPage(page); }
  public SetPage(page: number): boolean {
    if (page < 0 || page >= this.pageCount) {
      return false;
    }
    const oldPage = this._page;
    this._page = page;
    this.ChangePage(this._page, oldPage);
    return true;
  }
  public get page() { return this._page; }


  // SIZING


  public Resize(width: number, height: number) {
    this.pageWidth = width;
    this.pageHeight = height;

    if (this.isInitialized) {
      this.ClearDOM();
      this.CreateDOM();
    }
  }


  // DOM RENDERING ========================================================================


  // Renders 3 pages
  private CreateDOM() {
    if (this.pageCount === 0 || this.collection === undefined) {
      return;
    }
    let i = 0;
    while (i < this.pageCount) {
      this.BuildPage(i, i);
      i++;
    }
  }

  // Clears all pages out of the DOM, useful for re-rendering
  private ClearDOM() {
    const viewLength = this.viewContainer.length;
    for (let view of this.views) {
      if (view) {
        view.destroy();
      }
    }
    this.views = [];
  }


  // HTML CONSTRUCTION AND MANAGEMENT

  private BuildPage(pageNumber: number, loc: number) {
    if (pageNumber < 0 || pageNumber >= this.pageCount) {
      throw new Error('Attempted to create non-existent page: ' + pageNumber);
    }
    // Create the page given the template
    this.views[loc] = this.viewContainer.createEmbeddedView(
      this.template,
      new CarouselPage(this.collection[pageNumber], pageNumber, this));
    // Style the page accordingly
    for (let rootNode of this.views[loc].rootNodes) {
      rootNode.id = 'carouselDiv' + loc.toString();
      this.StyleAsPage(rootNode);
      this.StyleAtStackLocation(rootNode, (loc - this.page) + 1);
    }
  }

  // Styles a DOM element to be an absolute-positioned page-sized container
  protected StyleAsPage(pageElement: HTMLElement) {
    pageElement.style.display = 'block';
    pageElement.style.position = 'absolute';
    pageElement.style.width = this.pageWidth + 'px';
    pageElement.style.height = this.pageHeight + 'px';
  }

  // Styles a DOM element with an X location in the container
  protected StyleAtStackLocation(pageElement: HTMLElement, loc: number) {
    const xLocationInContainer = loc * this.pageWidth;
    pageElement.style.left = xLocationInContainer + 'px';
    if (xLocationInContainer !== this.pageWidth) {
      setTimeout(() => {
        const focusableEls = Array.prototype.slice.call(pageElement.
          querySelectorAll('a[href], area[href], button:not([disabled]), [tabindex="0"]'));
        for (let ele of focusableEls) {
          ele.setAttribute('tabindex', '-1');
        }
      }, 0);
    }
  }

  private StyleAtStackLocationNavigation(pageElement: HTMLElement, catoApplicable: boolean) {
    if (catoApplicable) {
      const focusableEls = Array.prototype.slice.call(pageElement.
        querySelectorAll('a[href], area[href], button:not([disabled]), [tabindex="0"]'));
      for (let ele of focusableEls) {
        ele.setAttribute('tabindex', '-1');
      }
    } else {
      const focusableEls = Array.prototype.slice.call(pageElement.
        querySelectorAll('a[href], button:not([disabled]), [tabindex="-1"]'));
      for (let ele of focusableEls) {
        ele.setAttribute('tabindex', '0');
      }
    }
  }


  // NAVIGATION

  // Updates rendering to display a new page
  private ChangePage(newPage: number, oldPage: number) {
    let i = 0;
    while (i < this.pageCount) {
      // methodname, timeout, param1, param2
      setTimeout(this.StyleAtStackLocationNavigation, 0, this.views[i].rootNodes[0], (i === newPage) ? false : true);
      i++;
    }
    if (newPage > oldPage) {
      this.updateNextPageStyle(newPage, oldPage);
    } else if ((newPage < oldPage) && !(newPage === 0 && (oldPage === this.pageCount - 1))) {
      this.updatePreviousPageStyle(newPage, oldPage);
    } else {
      this.updateFirstPageStyle();
    }
  }


  private updateNextPageStyle(newPage, oldPage) {
    while (oldPage < newPage) {
      let i = 0;
      while (i < this.pageCount) {
        this.views[i].rootNodes[0].style.left = (parseInt(this.views[i].rootNodes[0].style.left, 10) - this.pageWidth) + 'px';
        i++;
      }
      oldPage++;
    }
  }

  public prepareLastPageAnimation() {
    let i = 0;
    while (i < this.pageCount - 1) {
      this.views[i].rootNodes[0].style.left = ((i + 2) * this.pageWidth) + 'px';
      i++;
    }
  }

  private updatePreviousPageStyle(newPage, oldPage) {
    while (oldPage > newPage) {
      let i = 0;
      while (i < this.pageCount) {
        this.views[i].rootNodes[0].style.left = (parseInt(this.views[i].rootNodes[0].style.left, 10) + this.pageWidth) + 'px';
        i++;
      }
      oldPage--;
    }
  }

  private updateFirstPageStyle() {
    let i = 0;
    while (i < this.pageCount) {
      this.views[i].rootNodes[0].style.left = ((i + 1) * this.pageWidth) + 'px';
      i++;
    }
  }

}
